/**
 * Compact Circular Timer Component
 * Shows remaining time in a circular progress indicator with smooth animation.
 * - Shows minutes (e.g., "9m") when >= 1 minute remaining
 * - Shows seconds (e.g., "45s") when < 1 minute remaining
 * - Progress bar smoothly animates proportional to time elapsed
 * WCAG 2.1 AAA compliant with proper ARIA labels and screen reader announcements.
 */

import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "~/lib/utils";

interface CircularTimerProps {
  /** Remaining time in milliseconds */
  remainingTime: number;
  /** Total time in milliseconds (for progress calculation) */
  totalTime: number;
  /** Whether in warning state (< 1 minute) */
  isWarning: boolean;
  /** Whether timer has expired */
  isExpired: boolean;
  /** Whether timer is paused */
  isPaused?: boolean;
  /** Size of the circle in pixels */
  size?: number;
  /** Stroke width */
  strokeWidth?: number;
  /** Click handler for pause/resume */
  onClick?: () => void;
  /** Additional class names */
  className?: string;
}

export function CircularTimer({
  remainingTime,
  totalTime,
  isWarning,
  isExpired,
  isPaused = false,
  size = 40,
  strokeWidth = 3,
  onClick,
  className,
}: CircularTimerProps) {
  const { t } = useTranslation();

  // Calculate time remaining
  const totalSeconds = Math.max(0, Math.ceil(remainingTime / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  // SVG circle calculations
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Calculate current progress (1 = full, 0 = empty)
  // Progress represents how much of the circle should be filled
  // Example: 6 minutes remaining out of 10 = 60% progress = 60% of perimeter filled
  const progress = Math.max(0, Math.min(1, remainingTime / totalTime));

  // strokeDashoffset controls how much of the stroke is hidden
  // offset = 0 means full circle shown, offset = circumference means nothing shown
  const strokeOffset = circumference * (1 - progress);

  // Screen reader announcement for time warnings
  const lastAnnouncedRef = React.useRef<number>(-1);

  React.useEffect(() => {
    if (isExpired || isPaused) return;

    // Announce at 2 min, 1 min, 30 sec, 10 sec
    const thresholds = [120, 60, 30, 10];
    if (thresholds.includes(totalSeconds) && totalSeconds !== lastAnnouncedRef.current) {
      lastAnnouncedRef.current = totalSeconds;
    }
  }, [totalSeconds, isExpired, isPaused]);

  // WCAG 2.1 AAA compliant colors
  // Using CSS custom properties for theme consistency
  // Track: lighter shade for background, Progress: darker shade for contrast
  // AAA requires 7:1 for text, 3:1 minimum for UI components (we aim for 4.5:1+)
  const getColors = () => {
    if (isExpired) {
      return {
        // Red tones - track is light, progress is dark for visibility
        track: "hsl(var(--destructive) / 0.2)",
        progress: "hsl(var(--destructive))",
        text: "text-destructive",
      };
    }
    if (isWarning) {
      return {
        // Warning/amber tones - using theme warning color
        track: "hsl(var(--warning) / 0.25)",
        progress: "hsl(var(--warning))",
        text: "text-warning-foreground",
      };
    }
    return {
      // Primary blue tones - using theme primary color
      track: "hsl(var(--muted-foreground) / 0.2)",
      progress: "hsl(var(--primary))",
      text: "text-foreground",
    };
  };

  const colors = getColors();

  // Format display value
  // - "!" for expired
  // - "Xm" for minutes >= 1
  // - "Xs" for seconds < 60
  const getDisplayValue = () => {
    if (isExpired) return "!";
    if (minutes >= 1) return `${minutes}m`;
    return `${seconds}s`;
  };

  const displayValue = getDisplayValue();

  // Format time for accessibility
  const getTimeDescription = () => {
    if (minutes >= 1) {
      return t("onboarding.timer.minutesRemaining", {
        minutes,
        defaultValue: "{{minutes}} minutes remaining"
      });
    }
    return t("onboarding.timer.secondsRemaining", {
      seconds: totalSeconds,
      defaultValue: "{{seconds}} seconds remaining"
    });
  };

  // Accessible label
  const ariaLabel = isExpired
    ? t("onboarding.token.expired")
    : isPaused
    ? t("onboarding.timer.paused", {
        time: getTimeDescription(),
        defaultValue: "Paused: {{time}}"
      })
    : getTimeDescription();

  // Using div instead of button to allow this component to be placed
  // inside button elements (step indicators) without HTML nesting violations
  return (
    <div
      className={cn(
        "relative inline-flex items-center justify-center transition-transform hover:scale-105",
        "rounded-full",
        isPaused && "opacity-75",
        className
      )}
      role="timer"
      aria-live="polite"
      aria-atomic="true"
      aria-label={ariaLabel}
      title={onClick ? (isPaused ? t("onboarding.token.resumeTimer", "Click to resume timer") : t("onboarding.token.pauseTimer", "Click to pause timer")) : undefined}
    >
      <svg
        width={size}
        height={size}
        className={cn("transform -rotate-90", !isPaused && !isExpired && isWarning && "animate-pulse")}
        aria-hidden="true"
      >
        {/* Background fill for better contrast */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="hsl(var(--background))"
          strokeWidth={0}
        />
        {/* Track circle - subtle background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          style={{ stroke: colors.track }}
        />
        {/* Progress arc - proportional to remaining time */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={isExpired ? circumference : strokeOffset}
          style={{
            stroke: colors.progress,
            transition: 'stroke-dashoffset 1s linear'
          }}
        />
      </svg>

      {/* Center text */}
      <span
        className={cn(
          "absolute inset-0 flex items-center justify-center font-mono font-bold text-[10px]",
          colors.text
        )}
        aria-hidden="true"
      >
        {displayValue}
      </span>

      {/* Screen reader announcement */}
      <span className="sr-only" aria-live="assertive">
        {isWarning && !isExpired && t("onboarding.token.warningAnnouncement", {
          time: minutes >= 1 ? `${minutes}m` : `${totalSeconds}s`,
          defaultValue: "Warning: Only {{time}} remaining",
        })}
      </span>
    </div>
  );
}
